import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { Address, AddressSelector, PlaceType } from '../../selectors/AddressSelector';
import LocalPostOfficeIcon from '@material-ui/icons/LocalPostOffice';
import { POBoxTypeSelector } from '../../../lookupConfiguration/selectors/POBoxTypeSelector';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('OtherPartyPostalAddressPanel');

// Explicit request (EnquiryFormDrawer split): the other side's "Postal Address" accordion.
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface OtherPartyPostalAddressPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    homeCountry: string;
    isOtherPartyPoBoxRequired: (values: AnyObject) => boolean;
    requiredValidator: FieldValidator<any>;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
}

interface OtherPartyPostalAddressPanelBodyProps extends OtherPartyPostalAddressPanelProps {
    formValues: AnyObject;
    isPoBoxRequiredForValues: boolean;
}

const OtherPartyPostalAddressPanelBody: React.FC<OtherPartyPostalAddressPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, homeCountry, requiredValidator, createAddressSelectionHandler, createInputChangedHandler, onExpandChange,
        formValues,
        isPoBoxRequiredForValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            expanded={isExpanded}
            onChange={onExpandChange}
        // disabled={disabledAddress(values)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="other-party-postal-address-content"
                id="other-party-postal-address-header"
                className={classes.AccordionSummary}
            >
                <Typography className={classes.heading}>
                    <LocalPostOfficeIcon className={classes.headingIcon} />
                    Postal Address
                </Typography>

                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {/* {getAddressLabelForPanel} */}
                            {values.otherPartyPOBoxType ? `${values.otherPartyPOBoxType} ` : ''}
                            {values.otherPartyPOBox ? `${values.otherPartyPOBox}, ` : ''}
                            {values.otherPartyPostalSuburb ? `${values.otherPartyPostalSuburb}, ` : ''}
                            {values.otherPartyPostalState ? `${values.otherPartyPostalState}, ` : ''}
                            {values.otherPartyPostalPostCode ? `${values.otherPartyPostalPostCode}, ` : ''}
                            {values.otherPartyPostalCountry ? `${values.otherPartyPostalCountry}` : ''}
                        </div>
                    </div>
                </div>

            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid
                        item={true}
                        xs={4}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field
                            name="otherPartyPOBoxType"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <POBoxTypeSelector
                                    variant="outlined"
                                    size="small"
                                    // {...input}
                                    value={input.value ? { label: input.value, value: input.value } : null}
                                    label="PO Box Type"
                                    // required={true}
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    // tslint:disable-next-line: max-line-length
                                    onSelection={(selection: IAutoCompleteItem, name: string) => {
                                        input.onChange(selection ? selection.value : null);
                                    }}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Field is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={8} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyPOBox"
                            subscription={{ touched: true, error: true, value: true }}
                            validate={isPoBoxRequiredForValues ? requiredValidator : undefined}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Number"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    required={isPoBoxRequiredForValues ? true : false}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Number is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyPostalSuburb"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <AddressSelector
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    freeSolo={true}
                                    label="Suburb"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    homeCountry={homeCountry}
                                    // tslint:disable-next-line: max-line-length
                                    // placeTypes={['geocode', 'establishment']} // ['geocode', 'address', 'establishment', '(regions)', '(cities)'];
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Suburb is required'
                                            : ''
                                    }
                                    placeTypes={['(cities)']}
                                    onSelection={(placeType: PlaceType | null, address: Address, name: string) =>
                                                createAddressSelectionHandler(form, form.getState().values)(placeType, address, name)}
                                    onInputChanged={createInputChangedHandler(form, 'otherPartyPostalSuburb')}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyPostalState"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="State"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'State is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyPostalPostCode"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Postal Code"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Postal Code is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyPostalCountry"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Country"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    // disabled={props.sourceSystem?.toLowerCase() === 'leap'}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Country is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
OtherPartyPostalAddressPanelBody.displayName = 'OtherPartyPostalAddressPanelBody';

export const OtherPartyPostalAddressPanel: React.FC<OtherPartyPostalAddressPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <OtherPartyPostalAddressPanelBody
                {...panelProps}
                isPoBoxRequiredForValues={panelProps.isOtherPartyPoBoxRequired(values)}
                formValues={{
                    otherPartyPOBoxType: values.otherPartyPOBoxType,
                    otherPartyPOBox: values.otherPartyPOBox,
                    otherPartyPostalSuburb: values.otherPartyPostalSuburb,
                    otherPartyPostalState: values.otherPartyPostalState,
                    otherPartyPostalPostCode: values.otherPartyPostalPostCode,
                    otherPartyPostalCountry: values.otherPartyPostalCountry,
                    otherPartyNameGuid: values.otherPartyNameGuid,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
OtherPartyPostalAddressPanel.displayName = 'OtherPartyPostalAddressPanel';
